import { useMemo, type ReactNode } from 'react';
import { PlusIcon, UsersIcon } from '@heroicons/react/24/outline';
import {
    DataTable,
    type DataTableMode,
    type DataTablePagination,
    type DataTableRefs,
    type DataTableSearchConfig,
    type DataTableSort
} from '@odo/shared-ui';
import type { UserListItem, UsersSortBy } from '../../../services/users';
import { buildUserColumns } from './userColumns';

const SKELETON_ROW_COUNT = 9;
const TD_PADDING = 'px-3 first:pl-5 last:pr-4 overflow-hidden';

function SkeletonCell({ width }: { width: string }) {
    return <div className={`h-3.5 rounded bg-gray-200 animate-pulse ${width}`} />;
}

function SkeletonRows() {
    const widths = ['w-44', 'w-16', 'w-36', 'w-20'];
    return (
        <>
            {Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
                <tr key={i} className="h-16">
                    {widths.map((width, j) => (
                        <td key={j} className={`${TD_PADDING} align-middle`}>
                            <SkeletonCell width={width} />
                        </td>
                    ))}
                    <td className={TD_PADDING} />
                </tr>
            ))}
        </>
    );
}

function buildEmptyMessage(search: string, practiceName: string | null): string {
    const conditions: string[] = [];
    if (practiceName) conditions.push(`Practice: ${practiceName}`);

    if (search && conditions.length > 0) {
        return `No users match “${search}” with ${conditions.join(' and ')}.`;
    }
    if (search) {
        return `No users match “${search}”.`;
    }
    if (conditions.length > 0) {
        return `No users match ${conditions.join(' and ')}.`;
    }
    return 'No users yet.';
}

interface EmptyStateProps {
    search: string;
    practiceName: string | null;
    hasFilters: boolean;
    onClearFilters: () => void;
    onAddUser: () => void;
}

function EmptyState({
    search,
    practiceName,
    hasFilters,
    onClearFilters,
    onAddUser
}: EmptyStateProps) {
    return (
        <div className="flex flex-col items-center gap-3 text-center">
            <UsersIcon className="h-12 w-12 text-gray-300" strokeWidth={1} />
            <p className="text-sm text-gray-500">{buildEmptyMessage(search, practiceName)}</p>
            <div className="flex gap-2.5 mt-1">
                {hasFilters && (
                    <button
                        type="button"
                        onClick={onClearFilters}
                        className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm"
                    >
                        Clear filters
                    </button>
                )}
                <button
                    type="button"
                    onClick={onAddUser}
                    className="bg-black text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-gray-900 transition-colors inline-flex items-center gap-1.5"
                >
                    Add a user
                </button>
            </div>
        </div>
    );
}

interface UsersTableProps {
    users: UserListItem[];
    isLoading: boolean;
    search: string;
    practiceName: string | null;
    hasFilters: boolean;
    onClearFilters: () => void;
    onAddUser: () => void;
    onOpenUser: (user: UserListItem) => void;
    onEditUser: (user: UserListItem) => void;
    onSendPasswordReset: (user: UserListItem) => void;
    onDeleteUser: (user: UserListItem) => void;
    sort: DataTableSort<UsersSortBy>;
    mode: DataTableMode;
    onModeChange: (mode: DataTableMode) => void;
    pagination: DataTablePagination;
    onLoadMore: () => void;
    hasMore: boolean;
    isLoadingMore: boolean;
    searchConfig: DataTableSearchConfig;
    /**
     * Practice filter pill - server-searched (`usePracticeSearch`), so it
     * can't be expressed as a declarative `DataTableFilter` (those take a
     * fixed, client-searched option list). Lands via `DataTable`'s `toolbar`
     * escape hatch, same as CommunicationsTable's `toolbarSlot`.
     */
    toolbarSlot?: ReactNode;
    refs?: DataTableRefs;
}

export function UsersTable({
    users,
    isLoading,
    search,
    practiceName,
    hasFilters,
    onClearFilters,
    onAddUser,
    onOpenUser,
    onEditUser,
    onSendPasswordReset,
    onDeleteUser,
    sort,
    mode,
    onModeChange,
    pagination,
    onLoadMore,
    hasMore,
    isLoadingMore,
    searchConfig,
    toolbarSlot,
    refs
}: UsersTableProps) {
    const columns = useMemo(
        () => buildUserColumns({ onEditUser, onSendPasswordReset, onDeleteUser }),
        [onEditUser, onSendPasswordReset, onDeleteUser]
    );

    return (
        <DataTable
            label="Users"
            columns={columns}
            rows={users}
            getRowKey={(user) => user.id}
            rowAction={{ onActivate: onOpenUser }}
            rowHeightClassName="h-16"
            sort={sort}
            mode={mode}
            onModeChange={onModeChange}
            pagination={pagination}
            onLoadMore={onLoadMore}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
            isLoading={isLoading}
            loadingRows={<SkeletonRows />}
            emptyState={
                <EmptyState
                    search={search}
                    practiceName={practiceName}
                    hasFilters={hasFilters}
                    onClearFilters={onClearFilters}
                    onAddUser={onAddUser}
                />
            }
            emptyStateCellClassName="px-3 py-16"
            search={searchConfig}
            toolbar={toolbarSlot}
            actions={[
                {
                    key: 'add-user',
                    label: 'Add a user',
                    icon: <PlusIcon className="h-4 w-4" />,
                    variant: 'primary',
                    onClick: onAddUser
                }
            ]}
            refs={refs}
            className="overflow-hidden px-8 pb-8"
        />
    );
}
